import { useEffect, useMemo, useState } from "react";
import {
  FiRefreshCw, FiSearch, FiCalendar, FiTrendingUp,
  FiArrowUpRight, FiArrowDownLeft, FiFilter, FiX
} from "react-icons/fi";
import axios from "../api/axios";

/* ═══════════════════════════════════════════
   STYLES — NEUMORPHISM WITH VIBRANT COLORS
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .utx-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d0d5dc 100%);
    min-height: 100%;
  }
  .utx-root *, .utx-root *::before, .utx-root *::after { box-sizing: border-box; }
  .utx-root ::-webkit-scrollbar { width: 4px; height: 4px; }
  .utx-root ::-webkit-scrollbar-track { background: #e0e5ec; border-radius: 99px; }
  .utx-root ::-webkit-scrollbar-thumb { background: #3b82f6; border-radius: 99px; }

  .utx-page {
    max-width: 1280px; margin: 0 auto;
    padding: 24px 20px;
    display: flex; flex-direction: column; gap: 24px;
  }
  @media (max-width: 600px) { .utx-page { padding: 6px 5px; gap: 16px; } }

  /* ══════════════════════════════
     HERO - NEUMORPHISM
  ══════════════════════════════ */
  .utx-hero {
    background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%);
    border-radius: 9px;
    padding: 32px 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    position: relative;
    overflow: hidden;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.1);
  }
  .utx-hero::before {
    content: ''; position: absolute; top: -80px; right: -60px;
    width: 280px; height: 280px; border-radius: 50%;
    background: radial-gradient(circle, rgba(251,191,36,.2) 0%, transparent 70%);
    pointer-events: none;
  }
  .utx-hero::after {
    content: ''; position: absolute; bottom: -40px; left: 30%;
    width: 180px; height: 180px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }
  .utx-hero-left { position: relative; z-index: 1; }
  .utx-hero-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(251,191,36,.2);
    border: 1px solid rgba(251,191,36,.4);
    border-radius: 99px; padding: 5px 14px;
    font-size: 11px; font-weight: 700; color: #fcd34d;
    letter-spacing: .07em; text-transform: uppercase; margin-bottom: 12px;
    backdrop-filter: blur(10px);
  }
  .utx-badge-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #fbbf24;
    animation: utx-pulse 2s infinite;
  }
  @keyframes utx-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
  .utx-hero-title {
    font-size: clamp(20px, 4vw, 28px); font-weight: 900;
    color: #fff; line-height: 1.2; margin-bottom: 6px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }
  .utx-hero-sub { font-size: 13px; font-weight: 500; color: rgba(255, 255, 255, 0.8); }
  .utx-refresh-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 20px; border-radius: 30px; border: none; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 800;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    color: #1e3a8a;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.2), -4px -4px 8px rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
    position: relative; z-index: 1;
  }
  .utx-refresh-btn:hover {
    transform: translateY(-2px);
    box-shadow: 10px 10px 20px rgba(0, 0, 0, 0.25), -6px -6px 12px rgba(255, 255, 255, 0.15);
  }
  .utx-refresh-btn:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2), inset -4px -4px 8px rgba(255, 255, 255, 0.1);
  }
  .utx-refresh-btn.spinning svg {
    animation: utx-spin .7s linear infinite;
  }
  @keyframes utx-spin {
    to { transform: rotate(360deg); }
  }

  /* ══════════════════════════════
     SECTION LABEL
  ══════════════════════════════ */
  .utx-section-label {
    font-size: 11px;
    font-weight: 800;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: .09em;
    margin-bottom: -6px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .utx-section-label::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #3b82f6, transparent);
    border-radius: 99px;
  }

  /* ══════════════════════════════
     UPCOMING PAYOUTS STRIP - NEUMORPHISM
  ══════════════════════════════ */
  .utx-payout-strip {
    background: #e8edf5;
    border-radius:9px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
    overflow: hidden;
  }
  .utx-payout-header {
    padding: 16px 24px;
    background: linear-gradient(135deg, #1e3a8a, #1e40af);
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .utx-payout-header-title {
    font-size: 13px; font-weight: 800; color: #fff;
    text-transform: uppercase; letter-spacing: .07em;
    display: flex; align-items: center; gap: 7px;
  }
  .utx-payout-header-title svg { color: #fbbf24; }

  .utx-payout-list {
    display: flex; gap: 12px; padding: 20px 24px;
    overflow-x: auto; flex-wrap: nowrap;
  }
  .utx-payout-list::-webkit-scrollbar { height: 4px; }

  .utx-payout-item {
    flex-shrink: 0;
    background: linear-gradient(135deg, #fffbeb, #fef3c7);
    border-radius: 9px;
    padding: 14px 20px;
    min-width: 170px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.08), -4px -4px 8px rgba(255, 255, 255, 0.5);
    transition: all 0.2s ease;
  }
  .utx-payout-item:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.08), inset -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .utx-payout-plan {
    font-size: 11px; font-weight: 700; color: #92400e;
    text-transform: uppercase; letter-spacing: .06em;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .utx-payout-date { font-size: 14px; font-weight: 900; color: #1f2937; }

  .utx-payout-empty {
    padding: 20px 24px;
    font-size: 13px; font-weight: 600; color: #6b7280;
  }

  /* weekly credits info banner */
  .utx-credits-banner {
    display: flex; align-items: center; gap: 12px;
    padding: 16px 24px;
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    border-top: 1px solid rgba(59, 130, 246, 0.2);
  }
  .utx-credits-icon {
    width: 40px; height: 40px; border-radius: 20px; flex-shrink: 0;
    background: linear-gradient(135deg, #059669, #10b981);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 16px;
    box-shadow: 6px 6px 12px rgba(5, 150, 105, 0.3), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .utx-credits-label {
    font-size: 13px; font-weight: 800; color: #065f46;
  }
  .utx-credits-sub { font-size: 11px; font-weight: 500; color: #047857; margin-top: 2px; }

  /* ══════════════════════════════
     CONTROLS BAR - NEUMORPHISM
  ══════════════════════════════ */
  .utx-controls {
    background: #e8edf5;
    border-radius: 28px;
    padding: 20px 24px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
  .utx-search-wrap { position: relative; flex: 1; min-width: 220px; }
  .utx-search-icon {
    position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
    color: #3b82f6; pointer-events: none;
  }
  .utx-search-input {
    width: 100%; padding: 12px 16px 12px 42px;
    border: none; border-radius: 24px;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 500;
    color: #1f2937; outline: none;
    background: #e8edf5;
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.05), inset -4px -4px 8px rgba(255, 255, 255, 0.5);
    transition: all 0.2s;
  }
  .utx-search-input:focus {
    box-shadow: inset 6px 6px 12px rgba(0, 0, 0, 0.08), inset -6px -6px 12px rgba(255, 255, 255, 0.6);
    border: 1px solid #3b82f6;
  }
  .utx-search-input::placeholder { color: #9ca3af; }

  .utx-select {
    padding: 12px 40px 12px 16px;
    border: none; border-radius: 24px;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 600;
    color: #1f2937; outline: none; cursor: pointer;
    background: #e8edf5;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.1), -6px -6px 12px rgba(255, 255, 255, 0.5);
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%233b82f6' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    min-width: 160px;
  }
  .utx-select:active {
    transform: translateY(2px);
    box-shadow: inset 6px 6px 12px rgba(0, 0, 0, 0.08), inset -6px -6px 12px rgba(255, 255, 255, 0.5);
  }

  .utx-results-text {
    font-size: 12.5px; font-weight: 700; color: #6b7280;
    white-space: nowrap; margin-left: auto;
  }
  .utx-results-text strong { color: #3b82f6; }
  .utx-clear-btn {
    background: none; border: none; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 12px; font-weight: 700;
    color: #ef4444; display: inline-flex; align-items: center; gap: 4px;
    transition: color .2s; padding: 0;
  }
  .utx-clear-btn:hover { color: #dc2626; }

  /* ══════════════════════════════
     TABLE CARD - NEUMORPHISM
  ══════════════════════════════ */
  .utx-transactions-container {
    width: 100%;
  }

  /* Desktop Table View */
  .utx-table-wrapper {
    overflow-x: auto;
    border-radius: 28px;
    background: #e8edf5;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  .utx-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
  }

  .utx-table thead tr {
    background: linear-gradient(135deg, #1e3a8a, #1e40af);
  }

  .utx-table th {
    padding: 16px 18px;
    text-align: left;
    font-size: 10px;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase;
    letter-spacing: .08em;
  }

  .utx-table td {
    padding: 18px;
    border-bottom: 1px solid rgba(59, 130, 246, 0.15);
    vertical-align: middle;
  }

  .utx-table tbody tr {
    transition: all .15s;
  }
  .utx-table tbody tr:last-child td { border-bottom: none; }
  .utx-table tbody tr:hover {
    background: rgba(59, 130, 246, 0.05);
    transform: scale(1.01);
  }

  /* Date cell */
  .utx-tbl-date { font-size: 12px; font-weight: 700; color: #1f2937; white-space: nowrap; }
  .utx-tbl-time { font-size: 10.5px; font-weight: 600; color: #6b7280; margin-top: 3px; }

  /* Type chip */
  .utx-type-chip {
    display: inline-block; padding: 5px 12px;
    border-radius: 20px; font-size: 11px; font-weight: 800;
    text-transform: capitalize; white-space: nowrap;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .utx-type-chip.roi_income     { background: linear-gradient(135deg, #dcfce7, #bbf7d0); color: #15803d; }
  .utx-type-chip.level_income   { background: linear-gradient(135deg, #dbeafe, #bfdbfe); color: #1d4ed8; }
  .utx-type-chip.transfer       { background: linear-gradient(135deg, #ede9fe, #ddd6fe); color: #6d28d9; }
  .utx-type-chip.withdrawal     { background: linear-gradient(135deg, #fee2e2, #fecaca); color: #b91c1c; }
  .utx-type-chip.royalty_income { background: linear-gradient(135deg, #fef3c7, #fde68a); color: #92400e; }
  .utx-type-chip.deposit        { background: linear-gradient(135deg, #f0fdf4, #dcfce7); color: #166534; }

  /* Amount */
  .utx-amount { font-size: 15px; font-weight: 900; color: #1f2937; white-space: nowrap; }

  /* Wallet flow */
  .utx-flow { display: flex; flex-direction: column; gap: 4px; }
  .utx-flow-row {
    display: inline-flex; align-items: center; gap: 5px;
    font-size: 11px; font-weight: 700;
  }
  .utx-flow-row.from { color: #ef4444; }
  .utx-flow-row.to   { color: #10b981; }
  .utx-flow-row svg  { flex-shrink: 0; }

  /* Description */
  .utx-desc-main { font-size: 12.5px; font-weight: 600; color: #374151; }
  .utx-desc-main.weekly { color: #059669; font-weight: 800; }
  .utx-desc-sub  { font-size: 11px; font-weight: 500; color: #6b7280; margin-top: 3px; }
  .utx-desc-ref  { font-size: 10.5px; font-weight: 600; color: #9ca3af; margin-top: 2px; }
  .utx-desc-credited {
    display: inline-flex; align-items: center; gap: 4px;
    margin-top: 4px; padding: 4px 10px; border-radius: 20px;
    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
    font-size: 10.5px; font-weight: 700; color: #15803d;
    box-shadow: inset 1px 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Status pill */
  .utx-status {
    display: inline-flex; align-items: center;
    padding: 5px 12px; border-radius: 99px;
    font-size: 11px; font-weight: 800; text-transform: capitalize;
    white-space: nowrap;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .utx-status.approved  { background: linear-gradient(135deg, #dcfce7, #bbf7d0); color: #15803d; }
  .utx-status.rejected  { background: linear-gradient(135deg, #fee2e2, #fecaca); color: #b91c1c; }
  .utx-status.pending   { background: linear-gradient(135deg, #fef9c3, #fde68a); color: #a16207; }
  .utx-status.completed { background: linear-gradient(135deg, #dbeafe, #bfdbfe); color: #1d4ed8; }

  /* Empty row */
  .utx-empty-row td {
    text-align: center; padding: 60px 20px;
  }
  .utx-empty-icon { font-size: 48px; margin-bottom: 16px; }
  .utx-empty-text { font-size: 14px; font-weight: 600; color: #6b7280; }

  /* ══════════════════════════════
     MOBILE CARD VIEW
  ══════════════════════════════ */
  .utx-cards-wrapper {
    display: none;
    flex-direction: column;
    gap: 16px;
    padding: 4px;
  }

  @media (max-width: 899px) {
    .utx-table-wrapper {
      display: none;
    }
    .utx-cards-wrapper {
      display: flex;
    }
  }

  .utx-transaction-card {
    background: linear-gradient(135deg, #ffffff, #f3f6fc);
    border-radius: 9px;
    overflow: hidden;
    transition: all 0.2s ease;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .utx-transaction-card:active {
    transform: translateY(2px);
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.08), inset -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  .utx-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(135deg, #f8fafc, #f1f5f9);
    border-bottom: 1px solid rgba(59, 130, 246, 0.2);
  }

  .utx-card-date-day {
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
  }

  .utx-card-date-time {
    font-size: 11px;
    color: #6b7280;
    margin-top: 2px;
  }

  .utx-card-body {
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .utx-card-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }

  .utx-card-label {
    font-size: 10px;
    font-weight: 800;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-width: 100px;
  }

  .utx-card-value {
    flex: 1;
    font-size: 13px;
    color: #1f2937;
    word-break: break-word;
  }

  .utx-card-amount {
    font-size: 18px;
    font-weight: 900;
    color: #1f2937;
  }

  .utx-card-flow {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .utx-flow-arrow {
    color: #9ca3af;
    font-size: 12px;
  }

  /* Loading States */
  .utx-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px;
    gap: 16px;
    background: #e8edf5;
    border-radius: 28px;
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.05), inset -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  .utx-spinner {
    width: 44px;
    height: 44px;
    border: 3px solid rgba(59, 130, 246, 0.2);
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .utx-loading-text {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
  }

  /* Empty State */
  .utx-empty-state {
    text-align: center;
    padding: 60px 20px;
    background: #e8edf5;
    border-radius: 28px;
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.05), inset -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  .utx-empty-icon {
    font-size: 56px;
    margin-bottom: 16px;
  }

  .utx-empty-text {
    font-size: 14px;
    font-weight: 600;
    color: #6b7280;
  }

  /* ══════════════════════════════
     SKELETON LOADING
  ══════════════════════════════ */
  .utx-skeleton {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
  }
  .utx-skel {
    border-radius: 28px;
    background: linear-gradient(135deg, #e8edf5, #dce3ec);
    position: relative;
    overflow: hidden;
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.05), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }
  .utx-skel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(59, 130, 246, 0.1) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: utx-shimmer 1.4s infinite;
  }
  @keyframes utx-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }
  .utx-skel-hero { height: 130px; }
  .utx-skel-info { height: 100px; }
  .utx-skel-ctrl { height: 70px; }
  .utx-skel-tbl { height: 320px; }
`;

/* ═══════════════════════════════════════════
   TYPE → CSS CLASS MAP
═══════════════════════════════════════════ */
const typeClass = (type = "") => {
  const t = type.toLowerCase();
  if (t.includes("roi"))      return "roi_income";
  if (t.includes("level"))    return "level_income";
  if (t.includes("transfer")) return "transfer";
  if (t.includes("withdraw")) return "withdrawal";
  if (t.includes("royalty"))  return "royalty_income";
  if (t.includes("deposit"))  return "deposit";
  return "";
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const UserTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const fetchTransactions = async (fromBtn = false) => {
    try {
      if (fromBtn) setSpinning(true);
      setLoading(true);
      const [txRes, payoutRes] = await Promise.all([
        axios.get("/share/transactions"),
        axios.get("/share/my-payouts"),
      ]);
      setTransactions(txRes.data || []);
      setPayoutHistory(payoutRes.data || []);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setLoading(false);
      if (fromBtn) setTimeout(() => setSpinning(false), 700);
    }
  };

  useEffect(() => { fetchTransactions(); }, []);

  const typeOptions = useMemo(() =>
    Array.from(new Set(transactions.map(tx => tx.type).filter(Boolean))),
    [transactions]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return transactions.filter(tx => {
      const matchSearch = !q
        || tx.type?.toLowerCase().includes(q)
        || tx.description?.toLowerCase().includes(q)
        || String(tx.amount || "").includes(q)
        || String(tx.referenceId || "").toLowerCase().includes(q)
        || String(tx._id || "").toLowerCase().includes(q);
      const matchType = typeFilter === "all" || tx.type === typeFilter;
      return matchSearch && matchType;
    });
  }, [transactions, search, typeFilter]);

  const weeklyCredits = useMemo(() =>
    filtered.filter(tx => tx.type === "roi_income" && tx.description?.toLowerCase().includes("weekly share payout")),
    [filtered]
  );

  const upcomingPayouts = useMemo(() =>
    payoutHistory
      .filter(p => p.status === "active" && p.nextPayoutDate)
      .sort((a, b) => new Date(a.nextPayoutDate) - new Date(b.nextPayoutDate)),
    [payoutHistory]
  );

  const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const fmtTime = (d) => new Date(d).toLocaleTimeString("en-IN");

  const statusClass = (s) => {
    if (s === "approved") return "approved";
    if (s === "rejected") return "rejected";
    if (s === "pending")  return "pending";
    return "completed";
  };

  if (loading && !transactions.length) {
    return (
      <div className="utx-root">
        <style>{styles}</style>
        <div className="utx-skeleton">
          <div className="utx-skel utx-skel-hero" />
          <div className="utx-skel utx-skel-info" />
          <div className="utx-skel utx-skel-ctrl" />
          <div className="utx-skel utx-skel-tbl" />
        </div>
      </div>
    );
  }

  return (
    <div className="utx-root">
      <style>{styles}</style>
      <div className="utx-page">

        {/* Hero */}
        <div className="utx-hero">
          <div className="utx-hero-left">
            <div className="utx-hero-badge">
              <div className="utx-badge-dot" />
              Finance
            </div>
            <div className="utx-hero-title">My Transactions</div>
            <div className="utx-hero-sub">All transactions related to your account</div>
          </div>
          <button
            className={`utx-refresh-btn ${spinning ? "spinning" : ""}`}
            onClick={() => fetchTransactions(true)}
          >
            <FiRefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Upcoming Payouts */}
        <div className="utx-section-label">Payout Schedule</div>
        {/* <div className="utx-payout-strip"> */}
          {/* <div className="utx-payout-header">
            <div className="utx-payout-header-title">
              <FiCalendar size={14} /> Upcoming Weekly Payout Dates
            </div>
          </div>
          {upcomingPayouts.length > 0 ? (
            <div className="utx-payout-list">
              {upcomingPayouts.map(p => (
                <div className="utx-payout-item" key={p._id}>
                  <div className="utx-payout-plan">{p.planName || "Share Plan"}</div>
                  <div className="utx-payout-date">{fmtDate(p.nextPayoutDate)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="utx-payout-empty">No upcoming weekly payouts scheduled.</div>
          )} */}

          {/* Weekly credits info */}
          {/* <div className="utx-credits-banner">
            <div className="utx-credits-icon"><FiTrendingUp size={16} /></div>
            <div>
              <div className="utx-credits-label">Weekly Payout Credits: {weeklyCredits.length}</div>
              <div className="utx-credits-sub">EMI weekly credits are marked clearly in the Description column with credited date/time.</div>
            </div>
          </div> */}
        {/* </div> */}

        {/* Controls */}
        <div className="utx-controls">
          <div className="utx-search-wrap">
            <FiSearch size={14} className="utx-search-icon" />
            <input
              type="text" className="utx-search-input"
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by type, description, amount, reference…"
            />
          </div>

          <select className="utx-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            {typeOptions.map(type => (
              <option key={type} value={type}>{type.replace(/_/g, " ")}</option>
            ))}
          </select>

          <div className="utx-results-text">
            <strong>{filtered.length}</strong> of {transactions.length} records
          </div>

          {(search || typeFilter !== "all") && (
            <button className="utx-clear-btn" onClick={() => { setSearch(""); setTypeFilter("all"); }}>
              <FiX size={12} /> Clear
            </button>
          )}
        </div>

        {/* Transaction History */}
        <div className="utx-section-label">Transaction History</div>
        <div className="utx-transactions-container">
          {loading ? (
            <div className="utx-loading">
              <div className="utx-spinner" />
              <div className="utx-loading-text">Loading transactions…</div>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="utx-table-wrapper">
                <table className="utx-table">
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Wallet Flow</th>
                      <th>Description</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length > 0 ? filtered.map(tx => {
                      const isWeekly = tx.type === "roi_income" && tx.description?.toLowerCase().includes("weekly share payout");
                      return (
                        <tr key={tx._id}>
                          <td>
                            <div className="utx-tbl-date">{fmtDate(tx.createdAt)}</div>
                            <div className="utx-tbl-time">{fmtTime(tx.createdAt)}</div>
                          </td>
                          <td>
                            <span className={`utx-type-chip ${typeClass(tx.type)}`}>
                              {tx.type === "roi_income" ? "EMI Income" : (tx.type || "—").replace(/_/g, " ")}
                            </span>
                          </td>
                          <td>
                            <div className="utx-amount">₹{Number(tx.amount || 0).toLocaleString("en-IN")}</div>
                          </td>
                          <td>
                            <div className="utx-flow">
                              <span className="utx-flow-row from">
                                <FiArrowUpRight size={11} />
                                {(tx.fromWallet || "—").toUpperCase()}
                              </span>
                              <span className="utx-flow-row to">
                                <FiArrowDownLeft size={11} />
                                {(tx.toWallet || "—").toUpperCase()}
                              </span>
                            </div>
                          </td>
                          <td>
                            {isWeekly ? (
                              <>
                                <div className="utx-desc-main weekly">Weekly Payout Credited</div>
                                <div className="utx-desc-sub">{tx.description}</div>
                                <div className="utx-desc-credited">
                                  ✓ {fmtDate(tx.createdAt)} {fmtTime(tx.createdAt)}
                                </div>
                              </>
                            ) : (
                              <div className="utx-desc-main">{tx.description || "—"}</div>
                            )}
                            <div className="utx-desc-ref">Ref: {tx.referenceId || "—"}</div>
                          </td>
                          <td>
                            <span className={`utx-status ${statusClass(tx.approvalStatus)}`}>
                              {tx.approvalStatus || "completed"}
                            </span>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr className="utx-empty-row">
                        <td colSpan="6">
                          <div className="utx-empty-icon">📭</div>
                          <div className="utx-empty-text">
                            {search || typeFilter !== "all"
                              ? "No transactions match your search"
                              : "No transactions found"}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="utx-cards-wrapper">
                {filtered.length > 0 ? filtered.map(tx => {
                  const isWeekly = tx.type === "roi_income" && tx.description?.toLowerCase().includes("weekly share payout");
                  return (
                    <div key={tx._id} className="utx-transaction-card">
                      <div className="utx-card-header">
                        <div className="utx-card-date">
                          <div className="utx-card-date-day">{fmtDate(tx.createdAt)}</div>
                          <div className="utx-card-date-time">{fmtTime(tx.createdAt)}</div>
                        </div>
                        <span className={`utx-status ${statusClass(tx.approvalStatus)}`}>
                          {tx.approvalStatus || "completed"}
                        </span>
                      </div>

                      <div className="utx-card-body">
                        <div className="utx-card-row">
                          <div className="utx-card-label">Type</div>
                          <div className="utx-card-value">
                            <span className={`utx-type-chip ${typeClass(tx.type)}`}>
                              {tx.type === "roi_income" ? "EMI Income" : (tx.type || "—").replace(/_/g, " ")}
                            </span>
                          </div>
                        </div>

                        <div className="utx-card-row">
                          <div className="utx-card-label">Amount</div>
                          <div className="utx-card-value utx-card-amount">
                            ₹{Number(tx.amount || 0).toLocaleString("en-IN")}
                          </div>
                        </div>

                        {/* <div className="utx-card-row">
                          <div className="utx-card-label">Wallet Flow</div>
                          <div className="utx-card-value utx-card-flow">
                            <span className="utx-flow-row from">
                              <FiArrowUpRight size={11} />
                              {(tx.fromWallet || "—").toUpperCase()}
                            </span>
                            <span className="utx-flow-arrow">→</span>
                            <span className="utx-flow-row to">
                              <FiArrowDownLeft size={11} />
                              {(tx.toWallet || "—").toUpperCase()}
                            </span>
                          </div>
                        </div> */}

                        <div className="utx-card-row">
                          <div className="utx-card-label">Description</div>
                          <div className="utx-card-value">
                            {isWeekly ? (
                              <>
                                <div className="utx-desc-main weekly">Weekly Payout Credited</div>
                                <div className="utx-desc-sub">{tx.description}</div>
                                <div className="utx-desc-credited">
                                  ✓ {fmtDate(tx.createdAt)} {fmtTime(tx.createdAt)}
                                </div>
                              </>
                            ) : (
                              <div className="utx-desc-main">{tx.description || "—"}</div>
                            )}
                            {/* <div className="utx-desc-ref">Ref: {tx.referenceId || "—"}</div> */}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="utx-empty-state">
                    <div className="utx-empty-icon">📭</div>
                    <div className="utx-empty-text">
                      {search || typeFilter !== "all"
                        ? "No transactions match your search"
                        : "No transactions found"}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default UserTransactions;